import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  Checkbox,
  Alert,
  List,
  Progress,
  Tag,
} from 'antd';
import { ArrowLeftOutlined, LinkOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CrmContactLink from '@/components/crm/CrmContactLink';
import {
  useLead,
  useLeadActivities,
  useUpdateLeadStatus,
  useAssignLead,
  useConvertLead,
} from '@/hooks/useLeads';
import CrmActivityTimeline from '@/components/crm/activities/CrmActivityTimeline';
import CrmActivityForm from '@/components/crm/activities/CrmActivityForm';
import { useCreateOpportunity } from '@/hooks/useOpportunities';
import { useUsers } from '@/hooks/useUsers';
import { salesOwnerUserListFilters } from '@/constants/sales-owner-role-codes';
import { useAuthStore } from '@/store/authStore';
import DocumentList from '@/components/documents/DocumentList';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import leadLifecycleService from '@/services/leadLifecycle.service';
import api from '@/services/api.service';
import { useInstalledSolutionPacks } from '@/hooks/useInstalledSolutionPacks';
import { EPC_COMMERCIAL_PACK_KEYS } from '@/config/solutionPackNavGates';
import { EpcLeadCockpit } from '@/components/epc/commercial-ux/EpcLeadCockpit';
import { LeadConversionPreviewModal } from '@/components/epc/commercial-ux/LeadConversionPreviewModal';
import { LeadStatusReasonModal } from '@/components/crm/leads/LeadStatusReasonModal';
import { LEAD_CLOSED_STATUSES } from '@/services/lead.service';

const { Title, Text } = Typography;

const LEAD_STATUSES = ['New', 'Contacted', 'Qualified', 'Unqualified', 'Lost'];

const LeadDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [convertOpen, setConvertOpen] = useState(false);
  const [checkpointAck, setCheckpointAck] = useState<Record<string, boolean>>({});
  const [convertPreviewOpen, setConvertPreviewOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const { installedPackKeys } = useInstalledSolutionPacks();
  const epcCommercialEnabled =
    installedPackKeys != null &&
    EPC_COMMERCIAL_PACK_KEYS.some((k) => installedPackKeys.includes(k));
  const queryClient = useQueryClient();
  const { data: lead, isLoading } = useLead(id);
  const { data: activities = [], isLoading: actLoading } = useLeadActivities(id);
  const { data: users = [] } = useUsers(salesOwnerUserListFilters(lead?.assigned_to));
  const updateStatus = useUpdateLeadStatus();
  const assignLead = useAssignLead();
  const convertLead = useConvertLead();
  const createOpp = useCreateOpportunity();
  const canLogActivity = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.create'));

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const revenueServiceTypeLabel = useMemo(() => {
    if (!lead?.revenue_service_type_id) return '—';
    const r = revenueServiceTypes.find((x) => x.id === lead.revenue_service_type_id);
    return r ? `${r.service_code} — ${r.service_name}` : '—';
  }, [lead?.revenue_service_type_id, revenueServiceTypes]);

  const { data: scoreData } = useQuery({
    queryKey: ['lead-scores', id],
    queryFn: () => leadLifecycleService.getScores(id!),
    enabled: !!id,
  });

  const { data: checkpoint, refetch: refetchCheckpoint } = useQuery({
    queryKey: ['lead-checkpoint', id],
    queryFn: () => leadLifecycleService.getDecisionCheckpoint(id!),
    enabled: !!id,
  });

  const ownerDisplay = useMemo(() => {
    if (lead?.assignedUser) {
      return [lead.assignedUser.first_name, lead.assignedUser.last_name].filter(Boolean).join(' ') || lead.assignedUser.email || '—';
    }
    const u = users.find((x) => x.id === lead?.assigned_to);
    return u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—' : '—';
  }, [lead, users]);

  const handleLeadPrimaryCta = async (key: 'convert' | 'qualify' | 'disqualify' | 'open_opportunity' | 'none') => {
    if (!id || !lead) return;
    if (key === 'open_opportunity' && lead.converted_to_opportunity_id) {
      navigate(`/growth/opportunities/${lead.converted_to_opportunity_id}`);
      return;
    }
    if (key === 'qualify') {
      await updateStatus.mutateAsync({ id, status: 'Qualified' });
      return;
    }
    if (key === 'disqualify') {
      setPendingStatus('Unqualified');
      return;
    }
    if (key === 'convert') {
      await openConvert();
    }
  };

  const openConvert = async () => {
    if (id) await refetchCheckpoint();
    if (epcCommercialEnabled) {
      setConvertPreviewOpen(true);
    } else {
      setConvertOpen(true);
    }
  };

  const runConvert = async () => {
    if (!id || !lead) return;
    if (!lead.revenue_service_type_id) {
      Modal.error({
        title: 'Revenue service type required',
        content: 'Edit the lead and select a revenue service type before converting to an opportunity.',
      });
      return;
    }
    if (lead.is_converted && lead.converted_to_opportunity_id) {
      navigate(`/growth/opportunities/${lead.converted_to_opportunity_id}`);
      return;
    }
    const owner = lead.assigned_to || user?.id;
    if (!owner) {
      Modal.error({ title: 'Assign a sales owner', content: 'Set an owner on the lead before converting.' });
      return;
    }
    const created = await createOpp.mutateAsync({
      opportunity_name: `${lead.customer_name} — Opportunity`,
      customer_name: lead.customer_name,
      customer_id: lead.customer_id || undefined,
      contact_person: lead.contact_person,
      email: lead.email,
      phone: lead.phone,
      lead_id: lead.id,
      sales_owner: owner,
      industry: lead.industry,
      stage: 'Qualification',
      probability: 40,
      currency: 'AED',
      estimated_value: lead.annual_revenue != null ? Number(lead.annual_revenue) : undefined,
      revenue_service_type_id: lead.revenue_service_type_id || undefined,
    });
    await convertLead.mutateAsync({ id, opportunity_id: created.id });
    setConvertOpen(false);
    setConvertPreviewOpen(false);
    queryClient.invalidateQueries({ queryKey: ['lead-checkpoint', id] });
    Modal.success({
      title: 'Opportunity created',
      content: created.opportunity_number || created.id,
      okText: 'Open Opportunity',
      onOk: () => navigate(`/growth/opportunities/${created.id}`),
    });
  };

  const saveCheckpointAck = async () => {
    if (!id) return;
    await leadLifecycleService.saveCheckpointAck(id, checkpointAck);
    await refetchCheckpoint();
  };

  if (!id) return null;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/leads')}>
          Leads
        </Button>
      </Space>

      <Spin spinning={isLoading}>
        {!lead ? (
          <Text type="secondary">Lead not found.</Text>
        ) : (
          <>
            <Title level={3}>
              {lead.customer_name}{' '}
              <Text type="secondary" style={{ fontSize: 16 }}>
                {lead.lead_number}
              </Text>
            </Title>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <Card title="Details" style={{ marginBottom: 16 }}>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Sales team">{lead.sales_team_name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      {lead.lead_status}
                      {lead.status_reason && (
                        <Text type="secondary">
                          {' '}
                          — {lead.status_reason}
                          {lead.status_reason_notes ? `: ${lead.status_reason_notes}` : ''}
                        </Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer">
                      {lead.customer_id ? (
                        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/customers/${lead.customer_id}/360`)}>
                          Customer 360
                        </Button>
                      ) : (
                        <Text type="secondary">Not linked (edit the lead to pick an existing customer)</Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Priority">{lead.priority}</Descriptions.Item>
                    <Descriptions.Item label="Contact">{lead.contact_person || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <CrmContactLink kind="email" value={lead.email} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      <CrmContactLink kind="phone" value={lead.phone} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Source">{lead.source || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Industry">{lead.industry || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Revenue service type">{revenueServiceTypeLabel}</Descriptions.Item>
                    <Descriptions.Item label="Annual revenue">
                      {lead.annual_revenue != null ? Number(lead.annual_revenue).toLocaleString() : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Next follow-up">
                      {lead.next_follow_up_date
                        ? dayjs(lead.next_follow_up_date).format('DD MMM YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Assigned">
                      {lead.assignedUser
                        ? `${lead.assignedUser.first_name} ${lead.assignedUser.last_name || ''}`.trim()
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Converted">
                      {lead.is_converted ? (
                        <Button
                          type="link"
                          icon={<LinkOutlined />}
                          onClick={() =>
                            lead.converted_to_opportunity_id &&
                            navigate(`/growth/opportunities/${lead.converted_to_opportunity_id}`)
                          }
                        >
                          View opportunity
                        </Button>
                      ) : (
                        'No'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes">{lead.notes || '—'}</Descriptions.Item>
                  </Descriptions>

                  <Space wrap style={{ marginTop: 16 }}>
                    <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
                      Transfer team
                    </Button>
                    <SalesTeamTransferModal
                      open={transferOpen}
                      onClose={() => setTransferOpen(false)}
                      entityType="Lead"
                      entityId={lead.id}
                      entityLabel={lead.lead_number}
                      currentTeamName={lead.sales_team_name}
                    />
                    <TransferHistoryPanel entityType="Lead" entityId={lead.id} />
                    <Select
                      value={lead.lead_status}
                      style={{ minWidth: 160 }}
                      options={LEAD_STATUSES.map((s) => ({ label: s, value: s }))}
                      onChange={(s) =>
                        LEAD_CLOSED_STATUSES.includes(s)
                          ? setPendingStatus(s)
                          : updateStatus.mutate({ id: lead.id, status: s })
                      }
                    />
                    <Select
                      allowClear
                      placeholder="Assign sales owner…"
                      style={{ minWidth: 200 }}
                      value={lead.assigned_to || undefined}
                      notFoundContent="No users with a sales owner role"
                      options={users.map((u) => ({
                        label: `${u.first_name} ${u.last_name || ''}`.trim(),
                        value: u.id,
                      }))}
                      onChange={(uid) => uid && assignLead.mutate({ id: lead.id, user_id: uid })}
                    />
                    <Button
                      type="primary"
                      disabled={!!lead.is_converted}
                      loading={convertLead.isPending || createOpp.isPending}
                      onClick={() => (lead.is_converted ? null : openConvert())}
                    >
                      {lead.is_converted ? 'Already converted' : 'Convert to opportunity'}
                    </Button>
                    <Button onClick={() => navigate(`/growth/quotations?opportunity_id=`) } disabled>
                      {/* placeholder — requires opportunity */}
                      Quotations
                    </Button>
                  </Space>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    After conversion, open the opportunity for quotations and estimations filtered by opportunity.
                  </Text>
                </Card>
              </Col>

              <Col xs={24} lg={10}>
                {epcCommercialEnabled && (
                  <div style={{ marginBottom: 16 }}>
                    <EpcLeadCockpit
                      lead={lead}
                      ownerLabel={ownerDisplay}
                      onPrimaryAction={handleLeadPrimaryCta}
                      onOpenConversionPreview={() => {
                        void openConvert();
                      }}
                    />
                  </div>
                )}
                <Card title="NOBLEX Lead Score" style={{ marginBottom: 16 }}>
                  {scoreData?.scores ? (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        Quality <Progress percent={Number(scoreData.scores.lead_quality_score)} size="small" />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        Enterprise value <Progress percent={Number(scoreData.scores.enterprise_value_score)} size="small" />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        Strategic fit <Progress percent={Number(scoreData.scores.strategic_fit_score)} size="small" />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        Risk (lower is better) <Progress percent={Number(scoreData.scores.risk_score)} size="small" status="exception" />
                      </div>
                      <Tag>AI confidence: {scoreData.scores.ai_confidence_score}</Tag>
                      <Button size="small" style={{ marginLeft: 8 }} onClick={() => id && leadLifecycleService.calculateScore(id).then(() => queryClient.invalidateQueries({ queryKey: ['lead-scores', id] }))}>
                        Recalculate
                      </Button>
                    </>
                  ) : (
                    <Text type="secondary">Save discovery fields to calculate scores.</Text>
                  )}
                </Card>

                <Card title="Enterprise Decision Checkpoint" style={{ marginBottom: 16 }}>
                  {checkpoint && !checkpoint.all_pass && (
                    <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Complete checkpoint before converting to opportunity." />
                  )}
                  <List
                    size="small"
                    dataSource={checkpoint?.checks ?? []}
                    renderItem={(item) => (
                      <List.Item>
                        <Checkbox
                          checked={item.pass || checkpointAck[item.key]}
                          disabled={item.pass}
                          onChange={(e) => setCheckpointAck((p) => ({ ...p, [item.key]: e.target.checked }))}
                        >
                          <Tag color={item.pass ? 'green' : 'default'}>{item.category}</Tag> {item.label}
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                  <Button size="small" onClick={saveCheckpointAck} style={{ marginTop: 8 }}>
                    Save acknowledgements
                  </Button>
                </Card>

                <Card title="Activity timeline" loading={actLoading}>
                  <CrmActivityTimeline activities={activities} />
                  {canLogActivity && (
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                      items={[
                        {
                          key: 'log',
                          label: 'Log activity',
                          children: <CrmActivityForm link={{ lead_id: lead.id }} />,
                        },
                      ]}
                    />
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col span={24}>
                <Card title="Documents">
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <DocumentUpload entityType="Lead" entityId={lead.id} />
                    <DocumentList entityType="Lead" entityId={lead.id} />
                  </Space>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>

      <Modal
        title="Enterprise Decision Checkpoint — Convert lead"
        open={convertOpen}
        onOk={runConvert}
        onCancel={() => setConvertOpen(false)}
        okText="Create opportunity & link"
        confirmLoading={createOpp.isPending || convertLead.isPending}
        okButtonProps={{ disabled: checkpoint ? !checkpoint.all_pass : false }}
      >
        <Text>
          Confirm all checkpoint items before investing enterprise resources in this opportunity.
        </Text>
        {checkpoint && (
          <List
            style={{ marginTop: 12 }}
            size="small"
            dataSource={checkpoint.checks}
            renderItem={(c) => (
              <List.Item>
                <Tag color={c.pass ? 'green' : 'red'}>{c.pass ? 'OK' : 'Pending'}</Tag> {c.label}
              </List.Item>
            )}
          />
        )}
      </Modal>

      <LeadStatusReasonModal
        status={pendingStatus}
        leadName={lead?.customer_name}
        confirmLoading={updateStatus.isPending}
        onCancel={() => setPendingStatus(null)}
        onSubmit={(reason) => {
          if (!lead || !pendingStatus) return;
          updateStatus.mutate(
            { id: lead.id, status: pendingStatus, reason },
            { onSuccess: () => setPendingStatus(null) }
          );
        }}
      />

      <LeadConversionPreviewModal
        open={convertPreviewOpen}
        lead={lead ?? null}
        checkpointAllPass={checkpoint?.all_pass}
        loading={createOpp.isPending || convertLead.isPending}
        onCancel={() => setConvertPreviewOpen(false)}
        onConfirm={() => void runConvert()}
      />
    </div>
  );
};

export default LeadDetailPage;
